import React from 'react';
import { Box, Typography, Chip, alpha } from '@mui/material';
import { ShowChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, LineChart, Line, ReferenceLine,
} from 'recharts';
import { LEVEL_META, LevelBadge, VNIndexSkeleton, fmtPct, fmtScore } from './shared';

const MarketOverviewSection = ({ loading, market }) => {
  const { t } = useTranslation();

  if (loading && !market) {
    return <VNIndexSkeleton />;
  }

  if (!market?.vnindex) {
    return null;
  }

  const vn = market.vnindex;
  const breadth = market.breadth || {};
  const vnConfig = LEVEL_META[vn.dist_level] || LEVEL_META[1];
  const historyChart = (market.vnindex_history || []).map((item) => ({
    date: item.date?.slice(5),
    score: item.total_dist_score,
    level: item.dist_level,
    signals: item.active_signal_count,
  }));

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{
        borderRadius: 2,
        border: `1.5px solid ${alpha(vnConfig.color, 0.25)}`,
        bgcolor: '#fff',
        overflow: 'hidden',
        boxShadow: `0 2px 12px ${alpha(vnConfig.color, 0.06)}`,
      }}>
        <Box sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          bgcolor: alpha(vnConfig.color, 0.03),
        }}>
          <ShowChart sx={{ fontSize: 18, color: vnConfig.color }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b' }}>VN-Index</Typography>
          <LevelBadge level={vn.dist_level} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8' }}>{t('distribution.vnindex.distScore')}</Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 900, color: vnConfig.color, fontFamily: 'monospace', lineHeight: 1 }}>
            {fmtScore(vn.total_dist_score)}<span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8' }}>/14</span>
          </Typography>

          <Box sx={{ width: '1px', height: 22, bgcolor: alpha(vnConfig.color, 0.15) }} />

          {[
            { label: `VNI ${vn.close?.toLocaleString()}`, color: '#1e293b' },
            { label: `${t('distribution.vnindex.session')} ${fmtPct(vn.pct_change)}`, color: vn.pct_change < 0 ? '#ef4444' : '#10b981' },
            { label: `${t('distribution.vnindex.volume')} ×${vn.vol_ratio?.toFixed(2)}`, color: vn.vol_ratio >= 1.3 ? '#ef4444' : '#64748b' },
            { label: `${t('distribution.vnindex.fiveSession')} ${fmtPct(vn.ret_5)}`, color: vn.ret_5 < 0 ? '#ef4444' : '#10b981' },
            { label: `${t('distribution.vnindex.tenSession')} ${fmtPct(vn.ret_10)}`, color: vn.ret_10 < 0 ? '#ef4444' : '#10b981' },
            { label: `${t('distribution.vnindex.distCount')} ${vn.dist_count ?? 0}`, color: (vn.dist_count ?? 0) >= 4 ? '#ef4444' : '#64748b' },
            { label: `${t('distribution.vnindex.patterns')} ${vn.active_pattern_count ?? 0}`, color: (vn.active_pattern_count ?? 0) >= 2 ? '#7c3aed' : '#64748b' },
          ].map((item) => (
            <Typography key={item.label} sx={{ fontSize: '0.75rem', fontWeight: 700, color: item.color, fontFamily: 'monospace' }}>
              {item.label}
            </Typography>
          ))}

          <Box sx={{ width: '1px', height: 22, bgcolor: alpha(vnConfig.color, 0.15) }} />

          {[
            { label: 'MA10', ok: vn.above_ma10 },
            { label: 'MA20', ok: vn.above_ma20 },
            { label: 'MA50', ok: vn.above_ma50 },
          ].map(({ label, ok }) => (
            <Typography key={label} sx={{ fontSize: '0.68rem', fontWeight: 700, color: ok ? '#10b981' : '#ef4444' }}>
              {ok ? '▲' : '▼'}{label}
            </Typography>
          ))}

          {(vn.active_signals?.length > 0 || vn.active_patterns?.length > 0) && (
            <>
              <Box sx={{ width: '1px', height: 22, bgcolor: alpha(vnConfig.color, 0.15) }} />
              {(vn.active_signals || []).map((signal) => (
                <Chip
                  key={signal}
                  label={t(`distribution.signals.${signal}`, signal)}
                  size="small"
                  sx={{ fontSize: '0.56rem', height: 18, fontWeight: 600, bgcolor: alpha('#3b82f6', 0.08), color: '#3b82f6' }}
                />
              ))}
              {(vn.active_patterns || []).map((pattern) => (
                <Chip
                  key={pattern}
                  label={t(`distribution.patterns.${pattern}`, pattern)}
                  size="small"
                  sx={{ fontSize: '0.56rem', height: 18, fontWeight: 600, bgcolor: alpha('#7c3aed', 0.08), color: '#7c3aed' }}
                />
              ))}
            </>
          )}

          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', ml: 'auto' }}>{vn.date}</Typography>
        </Box>

        <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 240px' }, gap: 1.5, alignItems: 'stretch' }}>
          {historyChart.length > 1 && (
            <Box sx={{ borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', p: 1.5 }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('distribution.vnindex.scoreHistory')}
              </Typography>
              <Box sx={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                    <YAxis domain={[0, 'auto']} tick={{ fontSize: 9 }} />
                    <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <ReferenceLine y={1.5} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Lv1', fontSize: 8 }} />
                    <ReferenceLine y={5.5} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'Lv2', fontSize: 8 }} />
                    <ReferenceLine y={7.5} stroke="#f97316" strokeDasharray="3 3" label={{ value: 'Lv3', fontSize: 8 }} />
                    <Line type="monotone" dataKey="score" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} name="Score" />
                    <Line type="monotone" dataKey="signals" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Signals" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}

          {breadth.total_stocks > 0 && (
            <Box sx={{ borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', p: 1.5, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('distribution.breadth.title')} — {breadth.total_stocks} {t('distribution.breadth.stocks')}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
                {[
                  { label: t('distribution.breadth.distAndBreak'), value: `${breadth.pct_distribution}%`, color: breadth.pct_distribution >= 15 ? '#ef4444' : '#f97316' },
                  { label: t('distribution.breadth.selling'), value: `${breadth.pct_selling}%`, color: breadth.pct_selling >= 5 ? '#ef4444' : '#f59e0b' },
                  { label: t('distribution.breadth.avgScore'), value: breadth.avg_score?.toFixed(1), color: breadth.avg_score >= 5 ? '#ef4444' : '#64748b' },
                ].map((item) => (
                  <Box key={item.label} sx={{ bgcolor: '#fff', textAlign: 'center', py: 0.8 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: item.color, fontFamily: 'monospace', lineHeight: 1 }}>{item.value}</Typography>
                    <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600, mt: 0.3 }}>{item.label}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}>
                {[
                  { label: t('distribution.levels.shaking'), count: breadth.level_1_count, color: '#10b981' },
                  { label: t('distribution.levels.profitTaking'), count: breadth.level_2_count, color: '#f59e0b' },
                  { label: t('distribution.levels.distributing'), count: breadth.level_3_count, color: '#f97316' },
                  { label: t('distribution.levels.trendBreak'), count: breadth.level_4_count, color: '#ef4444' },
                ].map((item) => {
                  const pct = Math.round((item.count / breadth.total_stocks) * 100);
                  return (
                    <Box key={item.label}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: item.color }}>{item.label}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: item.color, fontFamily: 'monospace' }}>
                          {item.count} <span style={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600 }}>({pct}%)</span>
                        </Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 0.5, bgcolor: alpha(item.color, 0.1), overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 0.5, bgcolor: item.color, transition: 'width 0.3s', minWidth: pct > 0 ? 4 : 0 }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MarketOverviewSection;